<script>
    import { onMount } from 'svelte';

    let is_static = false;
    export { is_static as static };

    const STAR_LAYER_CONFIGS = [
        {
            min_size: 0.55,
            max_size: 1.45,
            drift_speed: 0,
            spread_x: 360,
            spread_y: 180,
            glow_radius: 8,
            flare_chance: 0,
            colors: ['#475569', '#64748b', '#94a3b8'],
            glows: ['#0f172a', '#1d4ed8', '#7c3aed']
        },
        {
            min_size: 0.9,
            max_size: 2.2,
            drift_speed: 1.6,
            spread_x: 260,
            spread_y: 160,
            glow_radius: 0,
            flare_chance: 0,
            colors: ['#cbd5e1', '#dbeafe', '#f5d0fe', '#fef3c7', '#fecdd3']
        },
        {
            min_size: 1.8,
            max_size: 3.8,
            drift_speed: 5.5,
            spread_x: 500,
            spread_y: 210,
            glow_radius: 0,
            flare_chance: 0,
            colors: ['#dbeafe', '#fef3c7', '#fecdd3']
        }
    ];

    const ACCENT_STARS = [
        {
            x: 0.16,
            y: 0.18,
            size: 1.1,
            color: '#dbeafe',
            glow: '#7dd3fc',
            glow_radius: 22
        },
        {
            x: 0.84,
            y: 0.15,
            size: 1.2,
            color: '#fecdd3',
            glow: '#f472b6',
            glow_radius: 24
        },
        {
            x: 0.76,
            y: 0.5,
            size: 1.45,
            color: '#fef3c7',
            glow: '#facc15',
            glow_radius: 28
        }
    ];

    const STAR_VERTICAL_COVERAGE = 0.7;
    const INTRO_STAGGER_MS = 900;
    const INTRO_FADE_MS = 180;
    const INTRO_TOTAL_MS = INTRO_STAGGER_MS + INTRO_FADE_MS;

    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
    const lerp = (start, end, amount) => start + (end - start) * amount;
    const wrap = (value, limit) => {
        if (!limit) {
            return 0;
        }

        return ((value % limit) + limit) % limit;
    };

    const create_rng = (seed = 1) => {
        let current = seed >>> 0;

        return () => {
            current = (current * 1664525 + 1013904223) >>> 0;
            return current / 4294967296;
        };
    };

    const hex_to_rgba = (hex, alpha = 1) => {
        const normalized = hex.replace('#', '');

        if (normalized.length !== 6) {
            return `rgb(255 255 255 / ${alpha})`;
        }

        const red = Number.parseInt(normalized.slice(0, 2), 16);
        const green = Number.parseInt(normalized.slice(2, 4), 16);
        const blue = Number.parseInt(normalized.slice(4, 6), 16);

        return `rgb(${red} ${green} ${blue} / ${alpha})`;
    };

    const pick = (values, rng) =>
        values[Math.floor(rng() * values.length)] || values[0];

    let root_element;
    let canvas_element;

    let ctx = null;
    let animation_frame = 0;
    let width = 0;
    let height = 0;
    let dpr = 1;
    let is_reduced_motion = false;
    let is_in_viewport = true;
    let is_document_visible = true;
    let static_plain_stars = [];
    let static_sprite_stars = [];
    let moving_plain_stars = [];
    let moving_sprite_stars = [];
    let static_accents = [];
    let static_canvas = null;
    let has_played_intro = false;
    let intro_started_at = 0;

    const get_intro_alpha = (elapsed, appear_delay) =>
        clamp((elapsed - appear_delay) / INTRO_FADE_MS, 0, 1);

    const draw_plain_star = (target_ctx, star, x, y) => {
        target_ctx.beginPath();
        target_ctx.arc(x * dpr, y * dpr, star.radius_px, 0, Math.PI * 2);
        target_ctx.fillStyle = star.color;
        target_ctx.fill();
    };

    const create_star_sprite = (star) => {
        const glow_radius = star.glow_radius * dpr;
        const core_radius = Math.max(1, star.size) * dpr;
        const sprite_size = Math.ceil(glow_radius * 2 + dpr * 8);
        const sprite = document.createElement('canvas');

        sprite.width = sprite_size;
        sprite.height = sprite_size;

        const sprite_ctx = sprite.getContext('2d', { alpha: true });

        if (!sprite_ctx) {
            return null;
        }

        const center = sprite_size / 2;
        const glow_gradient = sprite_ctx.createRadialGradient(
            center,
            center,
            0,
            center,
            center,
            glow_radius
        );

        glow_gradient.addColorStop(0, hex_to_rgba(star.glow, 0.18));
        glow_gradient.addColorStop(0.34, hex_to_rgba(star.glow, 0.1));
        glow_gradient.addColorStop(1, 'rgb(0 0 0 / 0)');
        sprite_ctx.fillStyle = glow_gradient;
        sprite_ctx.fillRect(0, 0, sprite_size, sprite_size);

        const core_gradient = sprite_ctx.createRadialGradient(
            center,
            center,
            0,
            center,
            center,
            core_radius * 2.8
        );

        core_gradient.addColorStop(0, 'rgb(255 255 255 / 0.98)');
        core_gradient.addColorStop(0.36, hex_to_rgba(star.color, 0.92));
        core_gradient.addColorStop(0.72, hex_to_rgba(star.color, 0.18));
        core_gradient.addColorStop(1, 'rgb(0 0 0 / 0)');
        sprite_ctx.fillStyle = core_gradient;
        sprite_ctx.fillRect(0, 0, sprite_size, sprite_size);

        return sprite;
    };

    const create_accent_sprite = (accent) => {
        const glow_radius = accent.glow_radius * dpr;
        const core_radius = accent.size * 1.4 * dpr;
        const flare_length = glow_radius * 0.94;
        const flare_width = Math.max(1, dpr);
        const sprite_size = Math.ceil(glow_radius * 2 + dpr * 8);
        const sprite = document.createElement('canvas');

        sprite.width = sprite_size;
        sprite.height = sprite_size;

        const sprite_ctx = sprite.getContext('2d', { alpha: true });

        if (!sprite_ctx) {
            return null;
        }

        const center = sprite_size / 2;
        const glow_gradient = sprite_ctx.createRadialGradient(
            center,
            center,
            0,
            center,
            center,
            glow_radius
        );

        glow_gradient.addColorStop(0, hex_to_rgba(accent.glow, 0.24));
        glow_gradient.addColorStop(0.42, hex_to_rgba(accent.glow, 0.12));
        glow_gradient.addColorStop(1, 'rgb(0 0 0 / 0)');
        sprite_ctx.fillStyle = glow_gradient;
        sprite_ctx.fillRect(0, 0, sprite_size, sprite_size);

        const cross_gradient = sprite_ctx.createLinearGradient(
            center - flare_length,
            center,
            center + flare_length,
            center
        );

        cross_gradient.addColorStop(0, 'rgb(255 255 255 / 0)');
        cross_gradient.addColorStop(0.5, hex_to_rgba(accent.color, 0.28));
        cross_gradient.addColorStop(1, 'rgb(255 255 255 / 0)');
        sprite_ctx.fillStyle = cross_gradient;
        sprite_ctx.fillRect(
            center - flare_length,
            center - flare_width / 2,
            flare_length * 2,
            flare_width
        );
        sprite_ctx.fillRect(
            center - flare_width / 2,
            center - flare_length,
            flare_width,
            flare_length * 2
        );

        const core_gradient = sprite_ctx.createRadialGradient(
            center,
            center,
            0,
            center,
            center,
            core_radius * 2.8
        );

        core_gradient.addColorStop(0, 'rgb(255 255 255 / 1)');
        core_gradient.addColorStop(0.34, hex_to_rgba(accent.color, 0.92));
        core_gradient.addColorStop(0.7, hex_to_rgba(accent.color, 0.14));
        core_gradient.addColorStop(1, 'rgb(0 0 0 / 0)');
        sprite_ctx.fillStyle = core_gradient;
        sprite_ctx.fillRect(0, 0, sprite_size, sprite_size);

        return sprite;
    };

    const draw_sprite = (target_ctx, sprite, x, y) => {
        target_ctx.drawImage(
            sprite,
            Math.round(x * dpr - sprite.width / 2),
            Math.round(y * dpr - sprite.height / 2)
        );
    };

    const draw_wrapped_star = (target_ctx, star, x, alpha = 1) => {
        target_ctx.globalAlpha = alpha;

        if (star.sprite) {
            draw_sprite(target_ctx, star.sprite, x, star.base_y);
        } else {
            draw_plain_star(target_ctx, star, x, star.base_y);
        }

        if (x < star.edge_padding) {
            if (star.sprite) {
                draw_sprite(target_ctx, star.sprite, x + width, star.base_y);
            } else {
                draw_plain_star(target_ctx, star, x + width, star.base_y);
            }
        }

        if (x > width - star.edge_padding) {
            if (star.sprite) {
                draw_sprite(target_ctx, star.sprite, x - width, star.base_y);
            } else {
                draw_plain_star(target_ctx, star, x - width, star.base_y);
            }
        }

        target_ctx.globalAlpha = 1;
    };

    const rebuild_static_canvas = () => {
        if (!ctx || !canvas_element) {
            return;
        }

        static_canvas = document.createElement('canvas');
        static_canvas.width = canvas_element.width;
        static_canvas.height = canvas_element.height;

        const static_ctx = static_canvas.getContext('2d', { alpha: true });

        if (!static_ctx) {
            static_canvas = null;
            return;
        }

        static_ctx.imageSmoothingEnabled = true;
        static_ctx.clearRect(0, 0, static_canvas.width, static_canvas.height);
        static_ctx.globalCompositeOperation = 'source-over';

        for (const star of static_plain_stars) {
            draw_wrapped_star(static_ctx, star, star.base_x);
        }

        if (static_sprite_stars.length || static_accents.length) {
            static_ctx.globalCompositeOperation = 'lighter';

            for (const star of static_sprite_stars) {
                draw_wrapped_star(static_ctx, star, star.base_x);
            }

            for (const accent of static_accents) {
                draw_sprite(
                    static_ctx,
                    accent.sprite,
                    accent.canvas_x,
                    accent.canvas_y
                );
            }
        }

        static_ctx.globalCompositeOperation = 'source-over';
    };

    const rebuild_scene = () => {
        if (!width || !height) {
            static_plain_stars = [];
            static_sprite_stars = [];
            moving_plain_stars = [];
            moving_sprite_stars = [];
            static_accents = [];
            static_canvas = null;
            return;
        }

        const coverage_height = height * STAR_VERTICAL_COVERAGE;
        const rng = create_rng(81423);

        static_plain_stars = [];
        static_sprite_stars = [];
        moving_plain_stars = [];
        moving_sprite_stars = [];

        for (const layer of STAR_LAYER_CONFIGS) {
            const column_count = Math.max(1, Math.ceil(width / layer.spread_x));
            const row_count = Math.max(
                1,
                Math.ceil(coverage_height / layer.spread_y)
            );

            for (let row_index = 0; row_index < row_count; row_index += 1) {
                for (
                    let column_index = 0;
                    column_index < column_count;
                    column_index += 1
                ) {
                    const size = lerp(
                        layer.min_size,
                        layer.max_size,
                        Math.pow(rng(), 1.18)
                    );
                    const base_x = Math.min(
                        width,
                        column_index * layer.spread_x + rng() * layer.spread_x
                    );
                    const base_y = Math.min(
                        coverage_height,
                        row_index * layer.spread_y + rng() * layer.spread_y
                    );
                    const star = {
                        base_x,
                        base_y,
                        size,
                        drift_speed:
                            layer.drift_speed * lerp(0.84, 1.18, rng()),
                        color: pick(layer.colors, rng),
                        glow: layer.glows ? pick(layer.glows, rng) : null,
                        glow_radius: layer.glow_radius
                    };
                    const drawable = {
                        ...star,
                        radius_px: Math.max(0.7, star.size) * dpr,
                        appear_delay: rng() * INTRO_STAGGER_MS,
                        edge_padding: star.glow_radius
                            ? Math.max(star.glow_radius + 4, star.size * 2.5)
                            : Math.max(4, star.size * 2),
                        sprite:
                            star.glow_radius > 0
                                ? create_star_sprite(star)
                                : null
                    };
                    const horizontal_inset = Math.min(
                        drawable.edge_padding,
                        Math.max(0, width / 2)
                    );
                    const vertical_inset = Math.min(
                        Math.max(6, star.size * 2),
                        Math.max(0, coverage_height / 2)
                    );

                    drawable.base_x = clamp(
                        drawable.base_x,
                        horizontal_inset,
                        Math.max(horizontal_inset, width - horizontal_inset)
                    );
                    drawable.base_y = clamp(
                        drawable.base_y,
                        vertical_inset,
                        Math.max(
                            vertical_inset,
                            coverage_height - vertical_inset
                        )
                    );

                    if (drawable.drift_speed === 0) {
                        if (drawable.sprite) {
                            static_sprite_stars.push(drawable);
                        } else {
                            static_plain_stars.push(drawable);
                        }
                    } else if (drawable.sprite) {
                        moving_sprite_stars.push(drawable);
                    } else {
                        moving_plain_stars.push(drawable);
                    }
                }
            }
        }

        static_accents = ACCENT_STARS.map((accent) => ({
            ...accent,
            canvas_x: accent.x * width,
            canvas_y: accent.y * height,
            appear_delay: rng() * INTRO_STAGGER_MS,
            sprite: create_accent_sprite(accent)
        })).filter((accent) => accent.sprite);

        rebuild_static_canvas();
    };

    const render_scene = (now = 0) => {
        if (!ctx || !canvas_element) {
            return;
        }

        if (!has_played_intro && !is_reduced_motion && !intro_started_at) {
            intro_started_at = now;
        }

        ctx.clearRect(0, 0, canvas_element.width, canvas_element.height);

        const intro_elapsed = intro_started_at ? now - intro_started_at : 0;
        const is_intro_active =
            !has_played_intro &&
            !is_reduced_motion &&
            intro_elapsed < INTRO_TOTAL_MS;

        if (!is_intro_active && !has_played_intro) {
            has_played_intro = true;
        }

        if (is_intro_active) {
            ctx.globalCompositeOperation = 'source-over';

            for (const star of static_plain_stars) {
                const alpha = get_intro_alpha(intro_elapsed, star.appear_delay);

                if (alpha > 0) {
                    draw_wrapped_star(ctx, star, star.base_x, alpha);
                }
            }

            for (const star of moving_plain_stars) {
                const alpha = get_intro_alpha(intro_elapsed, star.appear_delay);
                const drift_x = is_reduced_motion
                    ? 0
                    : (now * 0.001 * star.drift_speed) % width;
                const x = wrap(star.base_x - drift_x, width);

                if (alpha > 0) {
                    draw_wrapped_star(ctx, star, x, alpha);
                }
            }

            if (
                static_sprite_stars.length ||
                moving_sprite_stars.length ||
                static_accents.length
            ) {
                ctx.globalCompositeOperation = 'lighter';

                for (const star of static_sprite_stars) {
                    const alpha = get_intro_alpha(
                        intro_elapsed,
                        star.appear_delay
                    );

                    if (alpha > 0) {
                        draw_wrapped_star(ctx, star, star.base_x, alpha);
                    }
                }

                for (const star of moving_sprite_stars) {
                    const alpha = get_intro_alpha(
                        intro_elapsed,
                        star.appear_delay
                    );
                    const drift_x = is_reduced_motion
                        ? 0
                        : (now * 0.001 * star.drift_speed) % width;
                    const x = wrap(star.base_x - drift_x, width);

                    if (alpha > 0) {
                        draw_wrapped_star(ctx, star, x, alpha);
                    }
                }

                for (const accent of static_accents) {
                    const alpha = get_intro_alpha(
                        intro_elapsed,
                        accent.appear_delay
                    );

                    if (alpha > 0) {
                        ctx.globalAlpha = alpha;
                        draw_sprite(
                            ctx,
                            accent.sprite,
                            accent.canvas_x,
                            accent.canvas_y
                        );
                    }
                }
            }

            ctx.globalAlpha = 1;
            ctx.globalCompositeOperation = 'source-over';
            return;
        }

        if (static_canvas) {
            ctx.drawImage(static_canvas, 0, 0);
        }

        ctx.globalCompositeOperation = 'source-over';

        for (const star of moving_plain_stars) {
            const drift_x = is_reduced_motion
                ? 0
                : (now * 0.001 * star.drift_speed) % width;
            const x = wrap(star.base_x - drift_x, width);

            draw_wrapped_star(ctx, star, x);
        }

        if (moving_sprite_stars.length) {
            ctx.globalCompositeOperation = 'lighter';

            for (const star of moving_sprite_stars) {
                const drift_x = is_reduced_motion
                    ? 0
                    : (now * 0.001 * star.drift_speed) % width;
                const x = wrap(star.base_x - drift_x, width);

                draw_wrapped_star(ctx, star, x);
            }
        }

        ctx.globalCompositeOperation = 'source-over';
    };

    const stop_animation = () => {
        if (!animation_frame) {
            return;
        }

        cancelAnimationFrame(animation_frame);
        animation_frame = 0;
    };

    const render_frame = (now) => {
        render_scene(now);
        animation_frame = requestAnimationFrame(render_frame);
    };

    const sync_animation = () => {
        stop_animation();

        if (!ctx) {
            return;
        }

        render_scene(performance.now());

        if (
            is_reduced_motion ||
            !is_in_viewport ||
            !is_document_visible ||
            (!moving_plain_stars.length && !moving_sprite_stars.length)
        ) {
            return;
        }

        animation_frame = requestAnimationFrame(render_frame);
    };

    const resize_canvas = () => {
        if (!root_element || !canvas_element) {
            return;
        }

        const rect = root_element.getBoundingClientRect();
        const next_width = Math.round(rect.width);
        const next_height = Math.round(rect.height);

        if (!next_width || !next_height) {
            return;
        }

        width = next_width;
        height = next_height;
        dpr = clamp(window.devicePixelRatio || 1, 1, 1.5);

        canvas_element.width = Math.round(width * dpr);
        canvas_element.height = Math.round(height * dpr);
        canvas_element.style.width = `${width}px`;
        canvas_element.style.height = `${height}px`;

        rebuild_scene();
        sync_animation();
    };

    onMount(() => {
        if (!root_element || !canvas_element) {
            return undefined;
        }

        ctx = canvas_element.getContext('2d', {
            alpha: true,
            desynchronized: true
        });

        if (!ctx) {
            return undefined;
        }

        ctx.imageSmoothingEnabled = true;

        const reduced_motion_query = window.matchMedia(
            '(prefers-reduced-motion: reduce)'
        );
        const update_reduced_motion = () => {
            is_reduced_motion = reduced_motion_query.matches || is_static;

            if (is_reduced_motion) {
                has_played_intro = true;
            }

            sync_animation();
        };

        update_reduced_motion();

        if (reduced_motion_query.addEventListener) {
            reduced_motion_query.addEventListener(
                'change',
                update_reduced_motion
            );
        } else {
            reduced_motion_query.addListener(update_reduced_motion);
        }

        const resize_observer = new ResizeObserver(() => {
            resize_canvas();
        });
        resize_observer.observe(root_element);

        let intersection_observer;

        if ('IntersectionObserver' in window) {
            intersection_observer = new IntersectionObserver(
                ([entry]) => {
                    is_in_viewport = entry?.isIntersecting ?? true;
                    sync_animation();
                },
                {
                    threshold: 0
                }
            );
            intersection_observer.observe(root_element);
        }

        const handle_visibility_change = () => {
            is_document_visible = !document.hidden;
            sync_animation();
        };

        document.addEventListener('visibilitychange', handle_visibility_change);
        handle_visibility_change();
        resize_canvas();

        return () => {
            stop_animation();
            resize_observer.disconnect();
            intersection_observer?.disconnect();
            document.removeEventListener(
                'visibilitychange',
                handle_visibility_change
            );

            if (reduced_motion_query.removeEventListener) {
                reduced_motion_query.removeEventListener(
                    'change',
                    update_reduced_motion
                );
            } else {
                reduced_motion_query.removeListener(update_reduced_motion);
            }
        };
    });
</script>

<div class="hero-stars" aria-hidden="true" bind:this={root_element}>
    <div class="hero-stars__backdrop" />
    <canvas class="hero-stars__canvas" bind:this={canvas_element} />
</div>

<style>
    .hero-stars {
        position: absolute;
        inset: 0;
        overflow: hidden;
        pointer-events: none;
        isolation: isolate;
        contain: paint;
        -webkit-mask-image: linear-gradient(
            180deg,
            transparent 2%,
            rgb(0 0 0 / 0.84) 12%,
            black 22%,
            black 80%,
            rgb(0 0 0 / 0.84) 70%,
            transparent 90%
        );
        -webkit-mask-composite: source-in;
        mask-image: linear-gradient(
            180deg,
            transparent 2%,
            rgb(0 0 0 / 0.84) 12%,
            black 22%,
            black 80%,
            rgb(0 0 0 / 0.84) 70%,
            transparent 90%
        );
        mask-composite: intersect;
    }

    .hero-stars__backdrop,
    .hero-stars__canvas {
        position: absolute;
        inset: 0;
    }

    .hero-stars__backdrop {
        background: radial-gradient(
                circle at 50% 28%,
                rgb(76 29 149 / 0.3) 0%,
                rgb(76 29 149 / 0.2) 16%,
                transparent 38%
            ),
            radial-gradient(
                circle at 18% 18%,
                rgb(37 99 235 / 0.2) 0%,
                transparent 28%
            ),
            radial-gradient(
                circle at 82% 22%,
                rgb(190 24 93 / 0.12) 0%,
                transparent 32%
            );
    }

    .hero-stars__canvas {
        width: 100%;
        height: 100%;
        opacity: 0.96;
        transform: translateZ(0);
    }

    @media (max-width: 767px) {
        .hero-stars__backdrop {
            background: radial-gradient(
                    circle at 50% 30%,
                    rgb(76 29 149 / 0.3) 0%,
                    rgb(76 29 149 / 0.2) 18%,
                    transparent 90%
                ),
                radial-gradient(
                    circle at 18% 20%,
                    rgb(37 99 235 / 0.2) 0%,
                    transparent 70%
                ),
                radial-gradient(
                    circle at 80% 24%,
                    rgb(190 24 93 / 0.12) 0%,
                    transparent 80%
                );
        }
    }
</style>
